import { clsx, type ClassValue } from "clsx";
import { format } from "date-fns";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { validate as validateUuid, version as uuidVersion } from "uuid";
import type { ZodIssue } from "zod";

// Utility function for Tailwind CSS
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validates if a value is a valid UUIDv7 string
 * Compatible with useParams() return type (string | string[] | undefined)
 *
 * @param value - The value to validate (from useParams() or direct string)
 * @returns true if the value is a valid UUIDv7, false otherwise
 */
export function isValidId(
  value: string | string[] | null | undefined,
): value is string {
  if (!value || Array.isArray(value)) return false;
  if (!validateUuid(value)) return false;
  try {
    return uuidVersion(value) === 7;
  } catch {
    return false;
  }
}

/**
 * Validates if a string is a valid CUID (Collision-resistant Unique Identifier)
 *
 * CUID format: starts with a lowercase letter followed by base36 characters (a-z0-9)
 * Typical CUID2 length is 24-25 characters, but we use a more flexible pattern
 *
 * @param value - The string to validate
 * @returns true if the value is a valid CUID, false otherwise
 */
export function isValidCuid(value: string): boolean {
  if (!value || typeof value !== "string") return false;

  // CUID2 pattern: starts with lowercase letter, followed by base36 characters
  // Length typically 24-25 characters, but we allow 20-30 for flexibility
  const cuidPattern = /^[a-z][a-z0-9]{19,29}$/;
  return cuidPattern.test(value);
}

/**
 * Validates if a value is either a valid UUID (any version) or CUID
 * Used for route parameters that may contain either format
 * Compatible with useParams() return type (string | string[] | undefined)
 *
 * @param value - The value to validate (from useParams() or direct string)
 * @returns true if the value is a valid UUID or CUID, false otherwise
 */
export function isValidIdentifier(
  value: string | string[] | null | undefined,
): value is string {
  if (!value || typeof value !== "string") return false;

  // Check UUID first (includes UUIDv7)
  if (validateUuid(value)) return true;

  // Check CUID
  if (isValidCuid(value)) return true;

  return false;
}

// This check is super losey-goosey
export function isValidHttpsUrl(url: string): boolean {
  const httpsRegex =
    /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
  return httpsRegex.test(url);
}

/**
 * Generates a URL-friendly slug from a given string
 * @param text - The text to convert to a slug
 * @returns A URL-friendly slug
 */
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and hyphens
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
}

/**
 * Parse Prisma/ZenStack validation errors into a flat map of { fieldPath: message }.
 * Handles nested paths like ["addresses", 0, "streetLine1"] by mapping to
 * the last string segment (e.g., "streetLine1").
 */
export function parsePrismaValidationErrors(
  error: unknown,
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;

  const collect = (issues: ZodIssue[]) => {
    issues.forEach((issue) => {
      const path = issue?.path ?? [];
      const message = issue?.message ?? "Invalid value";
      const stringSegments = (path as Array<string | number>).filter(
        (p) => typeof p === "string",
      ) as string[];
      if (stringSegments.length === 0) return;
      // Prefer the last segment so ["addresses", 0, "zipCode"] → "zipCode"
      const field = stringSegments[stringSegments.length - 1] as string;
      fieldErrors[field] = message;
    });
  };

  if (err?.error?.rejectedByValidation && err?.error?.zodErrors?.issues) {
    collect(err.error.zodErrors.issues as ZodIssue[]);
  }
  if (
    err?.error?.error?.rejectedByValidation &&
    err?.error?.error?.zodErrors?.issues
  ) {
    collect(err.error.error.zodErrors.issues as ZodIssue[]);
  }
  if (err?.info?.rejectedByValidation && err?.info?.zodErrors?.issues) {
    collect(err.info.zodErrors.issues as ZodIssue[]);
  }

  return fieldErrors;
}

/**
 * Build a user-facing message and retry-ability from an error.
 */
export function extractPrismaErrorMessage(error: unknown): {
  message: string;
  retryable: boolean;
  errorCode: string;
} {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;
  const fieldErrors = parsePrismaValidationErrors(error);
  const count = Object.keys(fieldErrors).length;
  if (count >= 1) {
    return {
      message: `Please fix ${count} validation error${count > 1 ? "s" : ""}.`,
      retryable: false,
      errorCode: "HTTP_422",
    };
  }

  const errorCode = err?.error?.code || err?.info?.code;
  if (errorCode === "P2002") {
    return {
      message: "A record with this information already exists.",
      retryable: false,
      errorCode,
    };
  }
  if (errorCode === "P2003") {
    return {
      message: "This record cannot be created due to a missing reference.",
      retryable: false,
      errorCode,
    };
  }
  if (errorCode === "P2025") {
    return {
      message: "The record you're trying to update doesn't exist.",
      retryable: false,
      errorCode,
    };
  }
  if (errorCode === "P2027") {
    return {
      message: "Multiple records were found when only one was expected.",
      retryable: false,
      errorCode,
    };
  }

  return {
    message:
      err?.error?.message || err?.message || "An unexpected error occurred.",
    retryable: true,
    errorCode,
  };
}

/** Set react-hook-form field errors from validation error payload */
export function setFormErrorsFromPrismaError<TFormValues extends FieldValues>(
  form: UseFormReturn<TFormValues>,
  error: unknown,
): void {
  const fieldErrors = parsePrismaValidationErrors(error);
  Object.entries(fieldErrors).forEach(([field, message]) => {
    form.setError(field as Path<TFormValues>, { type: "server", message });
  });

  // Root-level message for general display
  const { message } = extractPrismaErrorMessage(error);
  try {
    // Ensure no lingering root-level errors (which could include field-level duplicates)
    form.clearErrors("root" as unknown as Path<TFormValues>);
    form.setError("root.server" as unknown as Path<TFormValues>, {
      type: "server",
      message,
    });
  } catch {
    /* no-op */
  }
}

/** True if error represents Prisma/ZenStack validation errors (commonly HTTP 422) */
export function isPrismaValidationError(error: unknown): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;
  return !!(
    (err?.error?.rejectedByValidation && err?.error?.zodErrors?.issues) ||
    (err?.error?.error?.rejectedByValidation &&
      err?.error?.error?.zodErrors?.issues) ||
    (err?.info?.rejectedByValidation && err?.info?.zodErrors?.issues)
  );
}

/** Convenience helper to process mutation errors and update a form */
export function handleFormSubmissionError<TFormValues extends FieldValues>(
  error: unknown,
  form: UseFormReturn<TFormValues>,
  onValidationError?: (message: string) => void,
  onOtherError?: (message: string) => void,
) {
  if (isPrismaValidationError(error)) {
    setFormErrorsFromPrismaError(form, error);
    const { message } = extractPrismaErrorMessage(error);
    onValidationError?.(message);
    return;
  }
  const { message } = extractPrismaErrorMessage(error);
  onOtherError?.(message);
}

// /**
//  * Parses Prisma and ZenStack validation errors and returns field-specific error messages
//  * @param error - The error object from TanStack Query mutation
//  * @returns Object with field paths as keys and error messages as values
//  */
// function parsePrismaValidationErrors(error: any): Record<string, string> {
//   const fieldErrors: Record<string, string> = {};

//   // Check if this is a Prisma validation error
//   if (error?.error?.rejectedByValidation && error?.error?.zodErrors?.issues) {
//     const issues = error.error.zodErrors.issues;

//     issues.forEach((issue: ZodIssue) => {
//       if (issue.path && issue.message) {
//         // Special handling for model-level validation errors that reference multiple fields
//         // For errors like ["reserveBidAmount", "startingBid"], we want to show the error
//         // on the primary field (first in the path)
//         const fieldPath = issue.path[0];
//         fieldErrors[fieldPath] = issue.message;
//       }
//     });
//   }

//   // Check if this is a ZenStack validation error (nested structure)
//   if (
//     error?.error?.error?.rejectedByValidation &&
//     error?.error?.error?.zodErrors?.issues
//   ) {
//     const issues = error.error.error.zodErrors.issues;

//     issues.forEach((issue: ZodIssue) => {
//       if (issue.path && issue.message) {
//         // Special handling for model-level validation errors that reference multiple fields
//         // For errors like ["reserveBidAmount", "startingBid"], we want to show the error
//         // on the primary field (first in the path)
//         const fieldPath = issue.path[0];
//         fieldErrors[fieldPath] = issue.message;
//       }
//     });
//   }

//   return fieldErrors;
// }

// /**
//  * Enhanced error message extraction from Prisma and ZenStack errors
//  * @param error - The error object from TanStack Query mutation
//  * @returns Object with message, retryable flag, and error code
//  */
// export function extractPrismaErrorMessage(error: any /*QueryError*/): {
//   message: string;
//   retryable: boolean;
//   errorCode?: string;
// } {
//   // Check for validation errors first (both Prisma and ZenStack)
//   console.error("Error:", error);
//   if (
//     error?.info?.rejectedByValidation ||
//     error?.info?.error?.rejectedByValidation
//   ) {
//     const fieldErrors = parsePrismaValidationErrors(error);
//     const errorCount = Object.keys(fieldErrors).length;

//     if (errorCount === 1) {
//       return {
//         message:
//           Object.values(fieldErrors)[0] ||
//           "An unexpected error occurred. Please try again.",
//         retryable: false, // Validation errors should be fixed by user, not retried
//         errorCode: undefined,
//       };
//     } else if (errorCount > 1) {
//       return {
//         message: `Please fix ${errorCount} validation errors in the form above.`,
//         retryable: false,
//         errorCode: undefined,
//       };
//     }
//   }

//   // Check for specific Prisma error codes
//   // Handle different error structures: error.error.code and error.info.code
//   const errorCode = error?.error?.code || error?.info?.code;
//   if (errorCode) {
//     switch (errorCode) {
//       case "P2002":
//         return {
//           message: "A record with this information already exists.",
//           retryable: false, // Unique constraint violations should not be retried
//           errorCode: "P2002",
//         };
//       case "P2003":
//         return {
//           message: "This record cannot be created due to a missing reference.",
//           retryable: false,
//           errorCode: "P2003",
//         };
//       case "P2025":
//         return {
//           message: "The record you're trying to update doesn't exist.",
//           retryable: false, // Could be a race condition, might work on retry, but we don't want infinite retries
//           errorCode: "P2025",
//         };
//       case "P2027":
//         return {
//           message: "Multiple records were found when only one was expected.",
//           retryable: false,
//           errorCode: "P2027",
//         };
//       default:
//         break;
//     }
//   }

//   // Return the main error message or a generic one - assume retryable for unknown errors
//   return {
//     message:
//       error?.error?.message ||
//       "An unexpected error occurred. Please try again.",
//     retryable: true,
//     errorCode: errorCode || undefined,
//   };
// }

// /**
//  * Sets form errors from Prisma validation errors with enhanced error messages
//  * @param form - React Hook Form instance
//  * @param error - The error object from TanStack Query mutation
//  */
// export function setFormErrorsFromPrismaError(form: UseFormReturn, error: any) {
//   const fieldErrors = parsePrismaValidationErrors(error);

//   Object.entries(fieldErrors).forEach(([fieldPath, message]) => {
//     // Enhance the error message with field name if it's generic
//     let enhancedMessage = message;
//     if (message.includes("required") || message.includes("invalid")) {
//       const fieldName = "This is dumb!"; // getFieldDisplayName(fieldPath);
//       enhancedMessage = `${fieldName}: ${message}`;
//     }

//     // Set error for top-level field (e.g., "streetLine2")
//     form.setError(fieldPath as any, {
//       type: "server",
//       message: enhancedMessage,
//     });

//     // Also set error for nested address fields used by some forms
//     // Backend returns field names for the Address model (e.g., "streetLine2"),
//     // but Create Listing form nests these under "address.streetLine2"
//     const addressNestedFields = new Set([
//       "streetLine1",
//       "streetLine2",
//       "streetLine3",
//       "city",
//       "region",
//       "postalCode",
//       "country",
//     ]);

//     if (addressNestedFields.has(fieldPath)) {
//       const nestedPath = `address.${fieldPath}`;
//       try {
//         form.setError(nestedPath as any, {
//           type: "server",
//           message: enhancedMessage,
//         });
//       } catch (_) {
//         // Ignore if the nested field is not registered in this form
//       }
//     }
//   });
// }

// /**
//  * Checks if an error is a Prisma or ZenStack validation error
//  * @param error - The error object to check
//  * @returns true if it's a Prisma or ZenStack validation error
//  */
// export function isPrismaValidationError(error: any): boolean {
//   return !!(
//     (error?.error?.rejectedByValidation && error?.error?.zodErrors?.issues) ||
//     (error?.error?.error?.rejectedByValidation &&
//       error?.error?.error?.zodErrors?.issues)
//   );
// }

// /**
//  * Handles form submission errors with comprehensive error handling
//  * @param error - The error from the mutation
//  * @param form - React Hook Form instance
//  * @param onValidationError - Callback for validation errors
//  * @param onOtherError - Callback for other errors
//  */
// export function handleFormSubmissionError(
//   error: any,
//   form: any,
//   onValidationError?: (message: string) => void,
//   onOtherError?: (message: string) => void,
// ) {
//   console.error("Form submission error:", error);

//   if (isPrismaValidationError(error)) {
//     // Handle validation errors
//     setFormErrorsFromPrismaError(form, error);
//     const { message } = extractPrismaErrorMessage(error);
//     onValidationError?.(message);
//   } else {
//     // Handle other types of errors
//     const { message } = extractPrismaErrorMessage(error);
//     onOtherError?.(message);
//   }
// }

/**
 * Validates and sanitizes a callback URL to prevent open redirect vulnerabilities
 *
 * Supports deep linking with search params and hash actions:
 * - `/listing/abc123` - Basic path
 * - `/listing/abc123?ref=email` - With search params
 * - `/listing/abc123#place-bid` - With hash action
 * - `/listing/abc123?ref=email#place-bid` - Full deep link
 *
 * @see ADR-018 - Type-Safe Deep Linking with OAuth Redirect Support
 *
 * @param url - The URL to validate (must be relative, starting with `/`)
 * @returns The sanitized URL with path, search params, and hash preserved, or null if invalid
 *
 * @example
 * ```typescript
 * validateCallbackUrl("/listing/abc123#place-bid")
 * // → "/listing/abc123#place-bid"
 *
 * validateCallbackUrl("/browse?sort=price:asc&page=2")
 * // → "/browse?sort=price:asc&page=2"
 *
 * validateCallbackUrl("https://evil.com/phishing")
 * // → null (absolute URLs rejected to prevent open redirects)
 * ```
 */
export function callbackUrlIsSafe(url: string | null | undefined): boolean {
  if (!url) return true; // empty url is safe

  // Reject absolute URLs, protocol-relative URLs, and invalid formats
  return url.startsWith("/");
}

/**
 * Ensures a PropertySeeker record exists for the given user ID
 * @param userId - The user ID to check/create PropertySeeker for
 * @param createPropertySeeker - The mutation function to create PropertySeeker
 * @param refetchPropertySeeker - The refetch function to get updated PropertySeeker data
 * @param extractPrismaErrorMessage - Function to extract Prisma error messages
 * @returns Promise<boolean> - True if PropertySeeker exists or was created successfully
 */
export async function ensurePropertySeekerExists(
  userId: string,
  createPropertySeeker: (data: any) => Promise<any>,
  refetchPropertySeeker: () => Promise<any>,
  extractPrismaErrorMessage: (error: any) => {
    message: string;
    retryable: boolean;
    errorCode?: string;
  },
): Promise<boolean> {
  try {
    await createPropertySeeker({
      data: {
        id: userId,
        createdById: userId, // Add the required createdById field
        biography: "",
        creditScoreRange: "NOT_PROVIDED",
        annualIncome: "NOT_PROVIDED",
        mostRecentEviction: "NOT_PROVIDED",
        mostRecentEvictionProvided: null,
      },
    });

    // Refetch to get the newly created record
    await refetchPropertySeeker();
    return true;
  } catch (error) {
    const { message, retryable } = extractPrismaErrorMessage(error);

    if (!retryable) {
      // For non-retryable errors like unique constraint violations (P2002),
      // the record likely already exists, so refetch to get the existing record
      console.log(
        "PropertySeeker record likely already exists (unique constraint), refetching existing record:",
        message,
      );
      await refetchPropertySeeker();
      return true;
    }

    // For retryable errors, log and return false to let caller handle
    console.error("Failed to create PropertySeeker record:", error);
    return false;
  }
}

/**
 * File validation utilities
 */
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

/**
 * Validate a file against specified constraints
 */
export function validateFile(
  file: File,
  constraints: {
    maxSize: number;
    allowedTypes: readonly string[] | string[];
    maxCount?: number;
  },
): FileValidationResult {
  const result: FileValidationResult = { isValid: true, warnings: [] };

  // Check file size
  if (file.size > constraints.maxSize) {
    result.isValid = false;
    result.error = `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the maximum allowed size of ${constraints.maxSize / 1024 / 1024}MB.`;
    return result;
  }

  // Check file type
  if (!constraints.allowedTypes.includes(file.type)) {
    result.isValid = false;
    result.error = `File type "${file.type}" is not supported. Allowed types: ${constraints.allowedTypes.join(", ")}.`;
    return result;
  }

  // Add warnings for large files
  // if (file.size > constraints.maxSize * 0.8) {
  //   result.warnings?.push(
  //     `File is close to size limit (${(file.size / 1024 / 1024).toFixed(1)}MB / ${constraints.maxSize / 1024 / 1024}MB)`,
  //   );
  // }

  return result;
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Get file extension from filename or MIME type
 */
export function getFileExtension(filename: string, mimeType?: string): string {
  // Try to get extension from filename first
  const filenameExt = filename.split(".").pop()?.toLowerCase();
  if (filenameExt) return filenameExt;

  // Fallback to MIME type mapping
  if (mimeType) {
    const mimeToExt: Record<string, string> = {
      "image/jpeg": "jpg",
      "image/jpg": "jpg",
      "image/png": "png",
      "image/webp": "webp",
      "image/gif": "gif",
      "application/pdf": "pdf",
      "video/mp4": "mp4",
      "video/webm": "webm",
    };
    return mimeToExt[mimeType] || "unknown";
  }

  return "unknown";
}

export const enumValues = <T extends Record<string, string>>(e: T) =>
  Object.values(e) as [T[keyof T], ...T[keyof T][]];

// Helper function to create a date rounded to the nearest 15-minute interval
export function createRoundedDate(baseDate: Date): Date {
  const rounded = new Date(baseDate);
  const minutes = rounded.getMinutes();
  const roundedMinutes = Math.round(minutes / 15) * 15;
  rounded.setMinutes(roundedMinutes, 0, 0);
  return rounded;
}
// Helper function to combine date and time into a Date object, ensuring 15-minute intervals
export function combineDateAndTime(date: Date, timeString: string): Date {
  const [hours, minutes] = timeString.split(":").map(Number);
  const combined = new Date(date);
  // Ensure minutes are only 0, 15, 30, or 45
  const validMinutes = [0, 15, 30, 45].includes(minutes ?? 0)
    ? (minutes ?? 0)
    : 0;
  combined.setHours(hours ?? 0, validMinutes, 0, 0);
  return combined;
}

// Helper function to extract time string from Date, ensuring 15-minute intervals
export function extractTimeString(date: Date): string {
  const minutes = date.getMinutes();
  // Round to nearest 15-minute interval (0, 15, 30, 45)
  const roundedMinutes = Math.round(minutes / 15) * 15;
  const adjustedDate = new Date(date);
  adjustedDate.setMinutes(roundedMinutes);
  return format(adjustedDate, "HH:mm");
}

// Helper function to set time to 00:00:00 for available from date
export function setDateToMidnight(date: Date): Date {
  const midnight = new Date(date);
  midnight.setHours(0, 0, 0, 0);
  return midnight;
}

// Helper function to format date
export function formatDate(date: Date | string | undefined): string {
  if (!date) return "Not provided";
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Invalid date";
  }
}

/**
 * Get display name for a user with proper fallback logic
 *
 * Fallback order:
 * 1. "firstName lastName" (Better Auth user model fields)
 * 2. user.name (OAuth provider name)
 * 3. user.email (if useEmailAsBackupIfAvailable is true)
 * 4. "Anonymous" (when all fields are empty)
 *
 * @param user - User object with optional name, firstName, lastName, email fields
 * @param useEmailAsBackupIfAvailable - Whether to use email as fallback (default: true)
 * @returns Display name for the user
 *
 * @see zschema/auth.zmodel - User model with firstName/lastName (lines 22-23)
 */
export function getUserDisplayName(
  user?: {
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null,
  useEmailAsBackupIfAvailable = true,
): string {
  if (!user) return "Anonymous";

  // Try firstName and lastName first
  const firstName = user.firstName?.trim() || "";
  const lastName = user.lastName?.trim() || "";
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) return fullName;

  // Try user.name (OAuth provider name)
  if (user.name?.trim()) return user.name.trim();

  // Try email if enabled
  if (useEmailAsBackupIfAvailable && user.email?.trim()) {
    return user.email.trim();
  }

  // Fall back to Anonymous
  return "Anonymous";
}
